import type { Package } from '@hapana/shared/schemas/packages'

/**
 * Normalizes an ISO date-only string (YYYY-MM-DD) so it parses at local noon instead of
 * UTC midnight. Without this, `new Date('2025-04-01')` is treated as UTC 00:00, which shifts
 * the displayed date one day back for users in UTC- timezones.
 * Non-date-only strings (e.g. full ISO datetimes) are returned unchanged.
 */
export const normalizeIsoDate = (dateStr: string): string =>
  /^\d{4}-\d{2}-\d{2}$/.test(dateStr) ? dateStr + 'T12:00:00' : dateStr

/**
 * Parses a date value that may be an ISO string or a millisecond timestamp string.
 * GQLoom serializes z.date() fields as epoch millisecond strings (e.g. "1768181264053"),
 * while z.string() date fields come as ISO strings (e.g. "2025-08-15").
 */
export const parseServiceDate = (value: string): Date => {
  const num = Number(value)
  if (!isNaN(num) && String(num) === value) return new Date(num)
  return new Date(value)
}

export const mapServiceStatusToPackageStatus = (
  status: string,
  canceledAt?: string | null,
  scheduledCancellation?: { status: string; cancellationDate: string } | null
): Package['status'] => {
  const normalized = status.toUpperCase()

  // Check if there's a scheduled cancellation (status = SCHEDULED in ServiceCancellation table)
  if (scheduledCancellation && scheduledCancellation.status === 'SCHEDULED') {
    return 'cancellationScheduled'
  }

  // Legacy check: cancellation is scheduled (future date) vs completed (past date)
  // This handles cases where canceledAt is set to a future date (old approach)
  if (normalized === 'CANCELED' && canceledAt) {
    const cancelDate = parseServiceDate(canceledAt)
    const now = new Date()
    if (cancelDate > now) {
      return 'cancellationScheduled'
    }
    return 'cancelled'
  }

  switch (normalized) {
    case 'ACTIVE':
      return 'active'
    case 'SUSPENDED':
      return 'suspended'
    case 'EXPIRED':
      return 'expired'
    case 'CANCELED':
      return 'cancelled'
    case 'COMPLETED':
      return 'completed'
    case 'PENDING_START_DATE':
    case 'PENDINGSTARTDATE':
      return 'pendingStartDate'
    default:
      return 'active'
  }
}

/**
 * GQty/Apollo errors have format "user message: {full JSON stacktrace}".
 * Returns only the human-readable prefix before the JSON object.
 */
export const stripGQtyError = (message: string): string => {
  const colonJsonIdx = message.indexOf(': {')
  return colonJsonIdx !== -1 ? message.slice(0, colonJsonIdx) : message
}

/**
 * For a package shared into the current location, returns the date its credits become
 * usable here, or null when there is no waiting period or it has already elapsed.
 *
 * The unlock date is computed server-side (`sharedHomeUsageUnlockDate`) from the live
 * ServiceCreditAllocation table so it matches the booking gate exactly — the cached
 * creditBalanceSnapshot can carry a stale validFrom and must not be used for this.
 */
export const getSharedHomeUsageUnlockDate = (pkg: Package): string | null => {
  const unlock = pkg.sharedHomeUsageUnlockDate
  if (!unlock) return null

  const today = new Date()
  today.setHours(12, 0, 0, 0)
  const unlockDate = parseServiceDate(normalizeIsoDate(unlock))
  if (isNaN(unlockDate.getTime()) || unlockDate <= today) return null

  return unlock
}

export const mapServiceTypeToPackageType = (type: string | null): Package['type'] => {
  switch (type) {
    case 'MEMBERSHIP':
      return 'membership'
    case 'PACKAGE':
      return 'package'
    default:
      return 'package'
  }
}
